import React, { useState, useEffect, useRef } from 'react';

const SearchIcon = ({ className = 'w-5 h-5', title = 'Search' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    className={className}
    fill="currentColor"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : 'presentation'}
  >
    {title ? <title>{title}</title> : null}
    <path d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z" />
  </svg>
);

const LocationSearch = ({ getWeather, compact = false }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const suggestionsId = compact ? 'location-suggestions-compact' : 'location-suggestions';
  const inputId = compact ? 'location-search-compact' : 'location';

  // Debounce search requests
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?` +
          `name=${encodeURIComponent(query)}` +
          `&count=5&language=en&format=json`
        );
        const data = await response.json();
        setSuggestions(data.results || []);
        setShowSuggestions(true);
        setActiveIndex(-1);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
        setActiveIndex(-1);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Handle suggestion selection
  const handleSelectSuggestion = (location) => {
    setQuery(`${location.name}, ${location.country_code}`);
    setSelectedLocation(location);
    setShowSuggestions(false);
    setActiveIndex(-1);

    // Auto-load weather on selection
    if (typeof getWeather === 'function') {
      const syntheticEvent = {
        preventDefault: () => {},
        target: {
          elements: {
            location: { value: `${location.name}, ${location.country_code}` },
          },
        },
      };
      getWeather(syntheticEvent, location);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        e.preventDefault();
        setShowSuggestions(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(suggestions.length - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(0, prev - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedLocation) {
      // Pass the selected location directly to getWeather
      getWeather(e, selectedLocation);
      setQuery('');
      setSelectedLocation(null);
    } else if (query.trim()) {
      // Fallback: try to find location from query
      getWeather(e, null);
      setQuery('');
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (compact) {
    // Compact version for header
    return (
      <form className="flex-1 md:max-w-[300px] md:min-w-[300px] lg:max-w-[400px] lg:min-w-[400px] relative flex items-center" onSubmit={handleSubmit}>
        <div className="relative w-full">
          <label htmlFor={inputId} className="sr-only">
            Search location
          </label>
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            name="location"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedLocation(null);
              setShowSuggestions(true);
            }}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search location..."
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-controls={suggestionsId}
            aria-activedescendant={
              activeIndex >= 0 ? `${suggestionsId}-option-${activeIndex}` : undefined
            }
            className="w-full p-4 pr-12 border border-gray-300 rounded-xl text-sm text-gray-800 bg-gray-50 opacity-70 outline-none transition-all focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 placeholder:text-gray-400"
          />
          <button
            type="submit"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-none border-none cursor-pointer p-1 text-gray-500 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 rounded-md"
            aria-label="Search"
          >
            <SearchIcon className="w-5 h-5" />
          </button>
          {showSuggestions && suggestions.length > 0 && (
            <div
              id={suggestionsId}
              role="listbox"
              aria-label="Location suggestions"
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-[10000] max-h-[300px] overflow-y-auto"
              ref={suggestionsRef}
            >
              {suggestions.map((location, index) => (
                <button
                  key={`${location.id || index}-${location.latitude}`}
                  id={`${suggestionsId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`w-full text-left py-3 px-4 border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                    index === activeIndex ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => handleSelectSuggestion(location)}
                >
                  <div className="text-sm font-medium text-gray-800">
                    {location.name}
                    {location.admin1 && (
                      <span className="text-gray-500 font-normal">, {location.admin1}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {location.country} {location.country_code && `(${location.country_code})`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </form>
    );
  }

  // Full version for form
  return (
    <form className="flex flex-col gap-6 grow" onSubmit={handleSubmit}>
      <div className="relative flex flex-col gap-1.5">
        <label htmlFor={inputId} className="text-lg text-gray-700 tracking-wide mb-2">Location</label>
        <div className="relative w-full">
          <input
            id={inputId}
            ref={inputRef}
            type="text"
            name="location"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedLocation(null);
              setShowSuggestions(true);
            }}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Enter city or zip code..."
            autoComplete="off"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showSuggestions && suggestions.length > 0}
            aria-controls={suggestionsId}
            aria-activedescendant={
              activeIndex >= 0 ? `${suggestionsId}-option-${activeIndex}` : undefined
            }
            className="w-full box-border p-4 rounded-xl border border-black/12 outline-none bg-white text-gray-800 text-base transition-all focus:border-cyan-500 focus:shadow-[0_0_0_3px_rgba(6,182,212,0.1)] placeholder:text-gray-400"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              id={suggestionsId}
              role="listbox"
              aria-label="Location suggestions"
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-black/12 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] z-[1000] max-h-[300px] overflow-y-auto"
              ref={suggestionsRef}
            >
              {suggestions.map((location, index) => (
                <button
                  key={`${location.id || index}-${location.latitude}`}
                  id={`${suggestionsId}-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`w-full text-left py-3 px-4 border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 ${
                    index === activeIndex ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => handleSelectSuggestion(location)}
                >
                  <div className="text-gray-800 text-[15px] font-medium">
                    {location.name}
                    {location.admin1 && (
                      <span className="text-gray-500">, {location.admin1}</span>
                    )}
                  </div>
                  <div className="text-gray-400 text-[13px] mt-0.5">
                    {location.country} {location.country_code && `(${location.country_code})`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <button 
        type="submit"
        className="w-full mt-3 rounded-xl border-none cursor-pointer py-3.5 px-5 text-white bg-gradient-to-r from-green-400 to-cyan-500 text-base font-medium tracking-wide shadow-[0_4px_12px_rgba(6,182,212,0.3)] flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(6,182,212,0.4)] hover:opacity-95 active:translate-y-0 active:shadow-[0_2px_8px_rgba(6,182,212,0.3)]"
      >
        <SearchIcon className="w-5 h-5" />
        Get Weather
      </button>
    </form>
  );
};

export default LocationSearch;

